// frontend/src/components/TransferenciaTerceirosModal/TransferenciaTerceirosModal.jsx

import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, DatePicker, Button, message, InputNumber, Row, Col } from 'antd';
import dayjs from 'dayjs';
import { getPortadores } from '../../services/milhasService';
import { createTransferenciaTerceiros } from '../../services/milhasService';
// 1. IMPORTAÇÃO DO TRATAMENTO DE ERRO
import { getErrorMessage } from '../../utils/errorHandler';
import MobileLaunchSheet from '../MobileLaunchSheet/MobileLaunchSheet';

const { Option } = Select;

const TransferenciaTerceirosModal = ({ visible, onClose, onSuccess, isMobile = false }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    
    const [portadores, setPortadores] = useState([]);
    const [loadingPortadores, setLoadingPortadores] = useState(false);
    
    const [selectedPortadorOrigem, setSelectedPortadorOrigem] = useState(null);
    const [selectedPortadorDestino, setSelectedPortadorDestino] = useState(null);
    const [programasComuns, setProgramasComuns] = useState([]);

    // Hook de mensagem para consistência
    const [messageApi, contextHolder] = message.useMessage();

    useEffect(() => {
        if (visible) {
            form.resetFields();
            form.setFieldsValue({ data_solicitacao: dayjs() });
            setSelectedPortadorOrigem(null);
            setSelectedPortadorDestino(null);
            setProgramasComuns([]);

            const fetchPortadores = async () => {
                setLoadingPortadores(true);
                try {
                    const res = await getPortadores();
                    setPortadores(res.data);
                } catch (error) {
                    // 2. USO NO FETCH
                    messageApi.error(getErrorMessage(error, 'Falha ao carregar a lista de portadores.'));
                    onClose();
                } finally {
                    setLoadingPortadores(false);
                }
            };
            fetchPortadores();
        }
    }, [visible, form, onClose, messageApi]);
    
    useEffect(() => {
        if (selectedPortadorOrigem && selectedPortadorDestino) {
            const portadorOrigem = portadores.find(p => p.id === selectedPortadorOrigem);
            const portadorDestino = portadores.find(p => p.id === selectedPortadorDestino);

            if (portadorOrigem && portadorDestino) {
                const programasOrigemIds = new Set(portadorOrigem.programas.map(p => p.programa));
                
                const comuns = portadorDestino.programas
                    .filter(p => programasOrigemIds.has(p.programa))
                    .map(p => ({ id: p.programa, nome: p.programa_nome }));
                
                const unicos = Array.from(new Map(comuns.map(item => [item['id'], item])).values());
                setProgramasComuns(unicos);
            }
        } else {
            setProgramasComuns([]);
        }
    }, [selectedPortadorOrigem, selectedPortadorDestino, portadores]);

    const handlePortadorOrigemChange = (portadorId) => {
        setSelectedPortadorOrigem(portadorId);
        form.setFieldsValue({ portador_destino: null, programa_fidelidade: null });
        setSelectedPortadorDestino(null);
    };

    const handlePortadorDestinoChange = (portadorId) => {
        setSelectedPortadorDestino(portadorId);
        form.setFieldsValue({ programa_fidelidade: null });
    };

    const handleFormSubmit = async (closeOnSuccess = true) => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            const payload = {
                ...values,
                data_solicitacao: values.data_solicitacao.format('YYYY-MM-DD'),
            };
            await createTransferenciaTerceiros(payload);
            
            onSuccess(closeOnSuccess, 'Transferência para terceiro registrada! Confirme na lista de movimentações para efetivá-la.');

            if (!closeOnSuccess) {
                // Manter portador origem e destino, limpar o resto
                const origem = values.portador_origem;
                const destino = values.portador_destino;
                const programa = values.programa_fidelidade; 

                form.resetFields();
                form.setFieldsValue({
                    portador_origem: origem,
                    portador_destino: destino,
                    programa_fidelidade: programa,
                    data_solicitacao: dayjs(),
                });
            }

        } catch (error) {
            console.error('Erro ao registrar transferência:', error);
            // 3. USO NO SUBMIT
            messageApi.error(getErrorMessage(error, 'Erro ao registrar a transferência.'));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = () => handleFormSubmit(true);
    const handleSubmitAndAddNew = () => handleFormSubmit(false);

    const portadoresDestinoFiltrados = portadores.filter(p => p.id !== selectedPortadorOrigem);

    const formInner = (
            <Form form={form} layout="vertical" name="transferencia_terceiros_form" size={isMobile ? 'large' : 'middle'}>
                <Row gutter={16}>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="portador_origem" label="Portador de Origem" rules={[{ required: true, message: 'Selecione o portador de origem.' }]}>
                            <Select placeholder="Selecione..." onChange={handlePortadorOrigemChange} loading={loadingPortadores} showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                                {portadores.map(p => <Option key={p.id} value={p.id}>{p.nome_completo}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="portador_destino" label="Portador de Destino" rules={[{ required: true, message: 'Selecione o portador de destino.' }]}>
                            <Select placeholder="Selecione..." disabled={!selectedPortadorOrigem} onChange={handlePortadorDestinoChange} showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                                {portadoresDestinoFiltrados.map(p => <Option key={p.id} value={p.id}>{p.nome_completo}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={isMobile ? 24 : 12}>
                         <Form.Item name="programa_fidelidade" label="Programa de Fidelidade" rules={[{ required: true, message: 'Selecione o programa.' }]}>
                            <Select placeholder="Selecione o programa em comum" disabled={!selectedPortadorOrigem || !selectedPortadorDestino} showSearch filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}>
                                {programasComuns.map(p => <Option key={p.id} value={p.id}>{p.nome}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="data_solicitacao" label="Data da Solicitação" rules={[{ required: true, message: 'A data é obrigatória.' }]}>
                            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="quantidade_milhas" label="Quantidade de Milhas" rules={[{ required: true, message: 'A quantidade é obrigatória.' }]}>
                            <InputNumber style={{ width: '100%' }} min={1} formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={value => value.replace(/\./g, '')} />
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                         <Form.Item name="custo_operacao" label="Custo da Operação (R$)" initialValue={0} rules={[{ required: true, message: 'O custo é obrigatório.' }]}>
                            <InputNumber 
                                prefix="R$ " 
                                style={{ width: '100%' }} 
                                precision={2} 
                                formatter={(value) => {
                                    if (value === '' || value === null || value === undefined) return '';
                                    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
                                }}
                                parser={(value) => {
                                    if (!value) return null;
                                    const onlyNumbers = value.replace(/[^\d]/g, '');
                                    if (!onlyNumbers) return null;
                                    return parseFloat(onlyNumbers) / 100;
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
    );

    return (
        <>
            {contextHolder}
            {isMobile ? (
                <MobileLaunchSheet
                    open={visible}
                    title="Transf. para Terceiro"
                    onCancel={onClose}
                    onSave={handleSubmit}
                    saveLoading={loading}
                    secondaryAction={
                        <Button type="default" loading={loading} onClick={handleSubmitAndAddNew} block size="large">
                            Salvar e Adicionar Outro
                        </Button>
                    }
                >
                    {formInner}
                </MobileLaunchSheet>
            ) : (
                <Modal
                    title="Nova Transferência para Terceiro"
                    open={visible}
                    onCancel={onClose}
                    width={800}
                    destroyOnClose
                    footer={[
                        <Button key="back" onClick={onClose}>Cancelar</Button>,
                        <Button key="add" type="default" loading={loading} onClick={handleSubmitAndAddNew}>Salvar e Adicionar Outro</Button>,
                        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>Salvar</Button>,
                    ]}
                >
                    {formInner}
                </Modal>
            )}
        </>
    );
};

export default TransferenciaTerceirosModal;